import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Wallet, 
  AlertCircle, 
  CheckCircle, 
  ExternalLink, 
  Loader2, 
  RefreshCw,
  ArrowLeft,
  Calendar,
  DollarSign,
  FileText,
  WifiOff,
  Clock,
  CreditCard,
  Info,
  AlertTriangle,
  Download,
  FileDown,
  Upload,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  Lock,
  LogIn
} from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import axios from 'axios';
import { goBack } from '../utils/navigation';
import { tituloDeuda, boletaConDescuento, leyendaDeudaTotal } from '../utils/deudaUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// URLs de los formularios de adhesión a débito automático
const FORMULARIO_DEBITO_DIRECTO = "https://customer-assets.emergentagent.com/job_95809ea8-41a4-4c57-bf50-f6d22700a8cc/artifacts/0fjp4v8g_F.-Solicitud-Debito-Directo.pdf";
const FORMULARIO_DEBITO_VISA = "https://customer-assets.emergentagent.com/job_95809ea8-41a4-4c57-bf50-f6d22700a8cc/artifacts/98tt2yw9_F.-Debito-automatico-CREDITO-VISA.pdf";

// Componente para el contenido desplegable de adhesión a débito automático
function AdhesionDebitoContent({ token }) {
  const [uploadingDirecto, setUploadingDirecto] = useState(false);
  const [uploadingVisa, setUploadingVisa] = useState(false);
  const [successDirecto, setSuccessDirecto] = useState(false);
  const [successVisa, setSuccessVisa] = useState(false);
  const [error, setError] = useState(null);
  
  const fileInputDirectoRef = useRef(null);
  const fileInputVisaRef = useRef(null);

  const handleFileUpload = async (file, tipo) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('El archivo debe ser un PDF');
      return;
    }
    
    setError(null);
    const setUploading = tipo === 'directo' ? setUploadingDirecto : setUploadingVisa;
    const setSuccess = tipo === 'directo' ? setSuccessDirecto : setSuccessVisa;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('tipo_debito', tipo);
      formData.append('archivo', file);
      
      await axios.post(`${API_URL}/antuar/adhesion-debito`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el formulario');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Podés adherir al débito automático completando el formulario, firmándolo y subiéndolo.
      </p>
      
      {error && (
        <div className="p-3 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Débito Directo */}
      <div className="bg-white rounded-lg border border-green-200 p-3">
        <p className="font-medium text-gray-900 text-sm mb-2">Débito Directo (Cuenta Bancaria)</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" size="sm" asChild className="flex-1">
            <a href={FORMULARIO_DEBITO_DIRECTO} target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4 mr-2" />
              Descargar
            </a>
          </Button>
          
          <input
            type="file"
            ref={fileInputDirectoRef}
            accept=".pdf"
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files[0], 'directo')}
          />
          
          <Button
            size="sm"
            className={`flex-1 ${successDirecto ? 'bg-green-600 hover:bg-green-700' : ''}`}
            disabled={uploadingDirecto}
            onClick={() => fileInputDirectoRef.current?.click()}
          >
            {uploadingDirecto ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
            ) : successDirecto ? (
              <><Check className="w-4 h-4 mr-2" />¡Enviado!</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />Subir firmado</>
            )}
          </Button>
        </div>
      </div>
      
      {/* Débito VISA */}
      <div className="bg-white rounded-lg border border-green-200 p-3">
        <p className="font-medium text-gray-900 text-sm mb-2">Débito Automático VISA</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" size="sm" asChild className="flex-1">
            <a href={FORMULARIO_DEBITO_VISA} target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4 mr-2" />
              Descargar
            </a>
          </Button>
          
          <input
            type="file"
            ref={fileInputVisaRef}
            accept=".pdf"
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files[0], 'visa')}
          />
          
          <Button
            size="sm"
            className={`flex-1 ${successVisa ? 'bg-green-600 hover:bg-green-700' : ''}`}
            disabled={uploadingVisa}
            onClick={() => fileInputVisaRef.current?.click()}
          >
            {uploadingVisa ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
            ) : successVisa ? (
              <><Check className="w-4 h-4 mr-2" />¡Enviado!</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />Subir firmado</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function EstadoCuentaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token: authToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estado, setEstado] = useState(null);
  const [adhesionExpanded, setAdhesionExpanded] = useState(false);
  const [planesPagoExpanded, setPlanesPagoExpanded] = useState(false);
  
  // Token de URL (acceso sin login desde email)
  const urlToken = searchParams.get('token');
  const isPublicAccess = !!urlToken;

  useEffect(() => {
    if (urlToken) {
      // Acceso público con token de email
      consultarEstadoPublico();
    } else if (authToken) {
      // Acceso autenticado normal
      consultarEstado();
    } else {
      setLoading(false);
    }
  }, [authToken, urlToken]);

  const consultarEstadoPublico = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/antuar/estado-cuenta-publico`, {
        params: { token: urlToken }
      });
      setEstado(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError({
          tipo: 'token_expirado',
          mensaje: 'El link ha expirado. Por favor, solicitá un nuevo email de estado de cuenta o iniciá sesión.'
        });
      } else {
        setError({
          tipo: 'conexion',
          mensaje: err.response?.data?.detail || 'Error al conectar con el servidor'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const consultarEstado = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/antuar/mi-estado-cuenta`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setEstado(response.data);
    } catch (err) {
      setError({
        tipo: 'conexion',
        mensaje: err.response?.data?.detail || 'Error al conectar con el servidor'
      });
    } finally {
      setLoading(false);
    }
  };

  // Separar cuotas vencidas (incluye multas) y por vencer
  const cuotasVencidas = estado?.cuotas_vencidas?.filter(c => c.es_cuota_mensual || c.es_multa || c.es_convenio) || [];
  const cuotasPorVencer = estado?.cuotas_por_vencer?.filter(c => c.es_cuota_mensual) || [];
  
  // Opciones de pago anticipado (no vencidas, no multas)
  const opcionesPago = estado?.cuotas_por_vencer?.filter(c => !c.es_cuota_mensual && !c.es_multa) || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <UserHeader />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => goBack()}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Wallet className="w-7 h-7 text-primary" />
                Estado de Cuenta
              </h1>
              <p className="text-gray-500 mt-1">Consulta informativa de tu situación en el Colegio Provincial</p>
            </div>
            
            {!loading && !error && estado && (
              <Button variant="outline" onClick={isPublicAccess ? consultarEstadoPublico : consultarEstado}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            )}
          </div>
          
          {/* Indicador de acceso público */}
          {isPublicAccess && !loading && !error && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
              <Lock className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="text-sm text-blue-800">
                  Acceso desde email • Link válido por 7 días
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/login')}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                <LogIn className="w-4 h-4 mr-1" />
                Iniciar sesión
              </Button>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <Card>
            <CardContent className="py-16 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-600">Consultando legajo profesional...</p>
              <p className="text-sm text-gray-400 mt-2">Esto puede tomar unos segundos</p>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {error && (
          <Card className="border-red-200">
            <CardContent className="py-8">
              <div className="text-center">
                {error.tipo === 'token_expirado' ? (
                  <>
                    <Clock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Link expirado</h3>
                    <p className="text-gray-600 mb-6">{error.mensaje}</p>
                    
                    <div className="flex justify-center gap-3">
                      <Button onClick={() => navigate('/login')}>
                        <LogIn className="w-4 h-4 mr-2" />
                        Iniciar sesión
                      </Button>
                      
                      <Button variant="outline" asChild>
                        <a href="https://www.antuar.com.ar/catspba/" target="_blank" rel="noopener noreferrer">
                          Ir a ANTUAR
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </a>
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error de conexión</h3>
                    <p className="text-gray-600 mb-6">{error.mensaje}</p>
                    
                    <div className="flex justify-center gap-3">
                      <Button onClick={isPublicAccess ? consultarEstadoPublico : consultarEstado}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reintentar
                      </Button>
                      
                      <Button variant="outline" asChild>
                        <a href="https://www.antuar.com.ar/catspba/" target="_blank" rel="noopener noreferrer">
                          Ir a ANTUAR
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </a>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No autenticado y sin token */}
        {!loading && !error && !estado && !authToken && !urlToken && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Iniciar sesión requerido</h3>
                <p className="text-gray-600 mb-6">
                  Para ver tu estado de cuenta necesitás iniciar sesión con tu cuenta del Colegio.
                </p>
                
                <div className="flex justify-center gap-3">
                  <Button onClick={() => navigate('/login')}>
                    <LogIn className="w-4 h-4 mr-2" />
                    Iniciar sesión
                  </Button>
                  
                  <Button variant="outline" asChild>
                    <a href="https://www.antuar.com.ar/catspba/" target="_blank" rel="noopener noreferrer">
                      Ir a ANTUAR directo
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resultado exitoso */}
        {estado?.success && (
          <>
            {/* 1. DATOS DEL MATRICULADO */}
            <Card className="mb-4">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    {estado.nombre && (
                      <>
                        <p className="text-sm text-gray-500">Matriculado/a</p>
                        <p className="font-semibold text-gray-900">{estado.nombre}</p>
                      </>
                    )}
                    <p className="text-sm text-gray-500">Matrícula: {estado.matricula}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {estado.fecha_consulta && (
                      <Badge variant="outline" className="text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {estado.fecha_consulta}
                      </Badge>
                    )}
                    {estado.tiene_plan_pago && (
                      <Badge className="bg-blue-100 text-blue-700">
                        <FileText className="w-3 h-3 mr-1" />
                        Plan de pago activo
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. INFORMACIÓN SOBRE DÉBITO AUTOMÁTICO */}
            <Card className="mb-4 border-blue-200 bg-blue-50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                  <CreditCard className="w-4 h-4" />
                  Información sobre Débito Automático
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="flex gap-2 p-2 bg-white rounded-lg border border-blue-100">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 text-xs">Débito con Tarjeta de DÉBITO</p>
                    <p className="text-xs text-gray-600">
                      Los débitos se realizan <strong>entre el 6 y el 12 de cada mes</strong>. 
                      Si tenés adhesión, y te figura pendiente de pago es posible que el pago aún no haya impactado.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 p-2 bg-white rounded-lg border border-blue-100">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 text-xs">Débito con Tarjeta de CRÉDITO VISA</p>
                    <p className="text-xs text-gray-600">
                      El cobro se realiza <strong>entre 48 y 72 horas antes de la fecha de vencimiento</strong>. 
                      Si te figura pendiente de pago antes del vencimiento, probablemente sea por esto. Volvé a verificar posterior a la fecha de vencimiento.
                    </p>
                  </div>
                </div>

                {/* Acordeón de Adhesión a Débito Automático - Solo si está logueado */}
                {!isPublicAccess && (
                  <div className="border-t border-blue-200 pt-2">
                    <button
                      onClick={() => setAdhesionExpanded(!adhesionExpanded)}
                      className="w-full flex items-center justify-between p-2 bg-green-100 hover:bg-green-200 rounded-lg border border-green-300 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <FileDown className="w-4 h-4 text-green-700" />
                        <span className="font-medium text-green-800 text-xs">Adhesión a Débito Automático</span>
                      </div>
                      {adhesionExpanded ? (
                        <ChevronUp className="w-4 h-4 text-green-700" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-green-700" />
                      )}
                    </button>
                    
                    {adhesionExpanded && (
                      <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                        <AdhesionDebitoContent token={authToken} />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 4. ESTADO DE CUENTA */}
            {/* Caso: SIN DEUDA VENCIDA - Mostrar que está al día */}
            {estado.total_deuda_vencida === 0 && (
              <Card className="mb-4 border-emerald-300 bg-emerald-50">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold text-emerald-800 mb-1">✓ Estás al día</p>
                      <p className="text-sm text-emerald-700">
                        No tenés cuotas vencidas. ¡Felicitaciones!
                      </p>
                      {estado.total_pendiente > 0 && (
                        <p className="text-sm text-gray-600 mt-2">
                          Tu próxima cuota es de <span className="font-medium">${estado.total_pendiente?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}</span>
                        </p>
                      )}
                    </div>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center bg-emerald-100">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Caso: CON DEUDA VENCIDA - Mostrar deuda */}
            {estado.total_deuda_vencida > 0 && (
              <Card className="mb-4 border-red-300">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1" data-testid="ec-deuda-titulo">{tituloDeuda(estado)}</p>
                      <p className="text-3xl font-bold text-red-600" data-testid="ec-deuda-monto">
                        ${(estado.total_deuda_vencida || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                        <p data-testid="ec-deuda-matricula">{estado.deuda_matricula > 0 ? <>Deuda de matrícula: <span className="font-medium text-red-600">${estado.deuda_matricula.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></> : <span className="text-green-700">Matrícula: ¡al día!</span>}</p>
                        {estado.deuda_convenio > 0 && (
                          <p data-testid="ec-deuda-convenio">Deuda de convenio: <span className="font-medium text-red-600">${(estado.deuda_convenio || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                        )}
                        {estado.deuda_otros > 0 && (
                          <p data-testid="ec-deuda-otros">Otras deudas (multas): <span className="font-medium text-red-600">${estado.deuda_otros.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                        )}
                      </div>
                    </div>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center bg-red-100">
                      <AlertTriangle className="w-8 h-8 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cuotas VENCIDAS detalle */}
            {cuotasVencidas.length > 0 && (
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Deuda ({cuotasVencidas.length} items)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {cuotasVencidas.map((cuota, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div>
                          <p className="font-medium text-gray-900">
                            {cuota.concepto}{cuota.periodo && !cuota.es_multa ? ` - ${cuota.periodo}` : ''}
                          </p>
                          {cuota.es_convenio && (
                            <p className="text-sm text-red-600">Cuota de convenio impaga</p>
                          )}
                          {!cuota.es_multa && !cuota.es_convenio && (
                            <p className="text-sm text-red-600">
                              Venció: {cuota.vencimiento_real || cuota.vencimiento} ({cuota.dias_mora || Math.abs(cuota.dias_para_vencer)} días de mora)
                            </p>
                          )}
                          {cuota.es_multa && (
                            <p className="text-sm text-red-600">Multa - Monto fijo</p>
                          )}
                        </div>
                        <p className="font-bold text-red-700 shrink-0">
                          ${cuota.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 5. PENDIENTE DE PAGO (no vencido) */}
            {estado.total_pendiente > 0 && (
              <Card className="mb-4 border-amber-200">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Cuota del mes en curso</p>
                      <p className="text-2xl font-bold text-amber-600">
                        ${estado.total_pendiente?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 }) || '0,00'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Matrícula del período corriente
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center bg-amber-100">
                      <Calendar className="w-7 h-7 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {estado.total_deuda_vencida > 0 && (
              <Card className="mb-4 border-red-500 bg-red-50" data-testid="ec-deuda-total-card">
                <CardContent className="py-6">
                  <p className="text-sm font-semibold text-red-800 mb-1 uppercase tracking-wide">
                    Deuda total <span className="text-xs normal-case font-normal">{leyendaDeudaTotal(estado)}</span>
                  </p>
                  <p className="text-3xl font-bold text-red-700" data-testid="ec-deuda-total-monto">
                    ${((estado.total_deuda_vencida || 0) + (estado.total_pendiente || 0)).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* 6. CUOTAS POR VENCER */}
            {cuotasPorVencer.length > 0 && (
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                    <Calendar className="w-5 h-5" />
                    Cuotas por vencer ({cuotasPorVencer.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {cuotasPorVencer.map((cuota, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div>
                          <p className="font-medium text-gray-900">{cuota.concepto} - {cuota.periodo}</p>
                          <p className="text-sm text-amber-700">
                            Vence: {cuota.vencimiento}
                            {cuota.dias_para_vencer !== null && (
                              <span className="ml-1">
                                ({cuota.dias_para_vencer === 0 ? 'Hoy' : `en ${cuota.dias_para_vencer} días`})
                              </span>
                            )}
                          </p>
                        </div>
                        <p className="font-bold text-amber-700">
                          ${cuota.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 7. OPCIONES DE PAGO ANTICIPADO (con bonificación) */}
            {opcionesPago.length > 0 && (
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-lg">Opciones de pago anticipado (con bonificación)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {opcionesPago.map((opcion, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">
                            {opcion.periodo.includes('/AA') ? 'Pago Anual' : 
                             opcion.periodo.includes('/S1') ? 'Pago Semestral 1' :
                             opcion.periodo.includes('/S2') ? 'Pago Semestral 2' :
                             opcion.periodo}
                          </p>
                          <p className="text-sm text-gray-500">
                            Vence: {opcion.vencimiento}
                            {opcion.dias_para_vencer !== null && opcion.dias_para_vencer >= 0 && (
                              <span className="ml-1 text-green-600">
                                ({opcion.dias_para_vencer === 0 ? 'Hoy' : `${opcion.dias_para_vencer} días restantes`})
                              </span>
                            )}
                          </p>
                        </div>
                        <p className="font-semibold text-gray-700">
                          ${opcion.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Boleta de Pago Total con descuento - EN ROJO */}
            {estado.boleta_pago_total?.tiene_boleta && estado.boleta_pago_total?.vigente && (
              <Card className="mb-4 border-red-300 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-red-800">
                    <DollarSign className="w-5 h-5" />
                    Boleta de Pago Total
                    {boletaConDescuento(estado) && <Badge className="bg-red-600 text-white ml-2">80% desc. en mora</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200">
                    <div>
                      <p className="font-medium text-gray-900">{boletaConDescuento(estado) ? "Pago total de deuda con descuento" : "Pago total de deuda"}</p>
                      {boletaConDescuento(estado) && (<p className="text-sm text-red-700 mt-1">
                        <strong>Incluye:</strong> Deuda anterior + mes en curso con 80% de descuento en intereses por mora
                      </p>)}
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-2">
                        <Calendar className="w-3 h-3" />
                        Vence: {estado.boleta_pago_total.vencimiento}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-red-700">
                        ${estado.boleta_pago_total.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* PLANES DE PAGO */}
            {estado.planes_pago?.disponible && estado.planes_pago?.opciones?.length > 0 && (
              <Card className="mb-4 border-blue-200">
                <CardHeader 
                  className="cursor-pointer hover:bg-blue-50 transition-colors"
                  onClick={() => setPlanesPagoExpanded(!planesPagoExpanded)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                      <CreditCard className="w-5 h-5" />
                      Plan de Pagos - Opciones
                    </CardTitle>
                    {planesPagoExpanded ? (
                      <ChevronUp className="w-5 h-5 text-blue-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Para gestionar el plan de pagos, debés ingresar al{' '}
                    <a 
                      href="https://www.antuar.com.ar/catspba" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Portal ANTUAR
                    </a>. El plan requiere la adhesión con Tarjeta de Crédito VISA para cobro de cuota de la moratoria + matrícula mensual.
                  </p>
                  {estado.planes_pago.deuda_sin_descuento && (
                    <p className="text-sm text-gray-500 mt-1">
                      Deuda sin descuento: ${estado.planes_pago.deuda_sin_descuento.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                    </p>
                  )}
                </CardHeader>
                {planesPagoExpanded && (
                  <CardContent>
                    <div className="space-y-2">
                      {estado.planes_pago.opciones.map((opcion, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <div>
                            <p className="font-medium text-gray-900">
                              {opcion.cantidad_cuotas} cuotas de ${opcion.importe_cuota.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                            </p>
                            <p className="text-sm text-blue-700">
                              {opcion.descuento_mora} desc. en mora{opcion.tiene_interes ? ' • Con interés' : ' • Sin interés'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-800">
                              Total: ${opcion.total.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Plan de pago vigente */}
            {estado.planes_pago?.tiene_plan_vigente && (
              <Card className="mb-4 border-green-200 bg-green-50">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3 text-green-800">
                    <CheckCircle className="w-5 h-5" />
                    <div>
                      <p className="font-medium">{estado.planes_pago.mensaje_plan_vigente || 'Ya tenés (o tuviste recientemente) un Plan de Pago vigente. Podés descargar el convenio desde la web de ANTUAR. Si creés que es un error, contactate con el Colegio.'}</p>
                      <a 
                        href="https://www.antuar.com.ar/catspba" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-green-700 underline"
                      >
                        Descargá el convenio desde ANTUAR
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Boleta vencida */}
            {estado.boleta_pago_total?.tiene_boleta && !estado.boleta_pago_total?.vigente && (
              <Card className="mb-4 border-gray-300 bg-gray-50">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3 text-gray-500">
                    <AlertCircle className="w-5 h-5" />
                    <p className="text-sm">
                      La oferta de pago total con descuento venció el {estado.boleta_pago_total.vencimiento}. 
                      Consultá en ANTUAR por nuevas opciones.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sin deuda */}
            {cuotasVencidas.length === 0 && cuotasPorVencer.length === 0 && (
              <Card className="mb-4 border-emerald-200">
                <CardContent className="py-8 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">¡Sin deuda!</h3>
                  <p className="text-gray-600">No tenés cuotas pendientes de pago.</p>
                </CardContent>
              </Card>
            )}

            {/* LINK A ANTUAR */}
            <Card className="mb-4 bg-gray-50 border-gray-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 font-medium mb-2">
                      Esta información es solo de consulta
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      Para realizar pagos, gestionar planes de pago o descargar boletas, 
                      ingresá al portal oficial de ANTUAR.
                    </p>
                    
                    {/* Datos necesarios para ingresar a ANTUAR */}
                    <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-4">
                      <p className="text-sm font-semibold text-amber-800 mb-1">
                        ⚠️ Para ingresar a ANTUAR vas a necesitar:
                      </p>
                      <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                        <li><strong>Número de matrícula</strong></li>
                        <li><strong>DNI</strong> (sin puntos)</li>
                        <li><strong>Fecha de nacimiento</strong> (no la fecha de hoy)</li>
                      </ul>
                    </div>
                    
                    <Button asChild className="bg-blue-600 hover:bg-blue-700">
                      <a href="https://www.antuar.com.ar/catspba/" target="_blank" rel="noopener noreferrer">
                        Ir a ANTUAR - Portal de Pagos
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Error de ANTUAR */}
        {estado && !estado.success && (
          <Card className="border-red-200">
            <CardContent className="py-8 text-center">
              <WifiOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No se pudo consultar</h3>
              <p className="text-gray-600 mb-6">{estado.error}</p>
              
              <div className="flex justify-center gap-3">
                <Button onClick={consultarEstado}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reintentar
                </Button>
                <Button variant="outline" asChild>
                  <a href="https://www.antuar.com.ar/catspba/" target="_blank" rel="noopener noreferrer">
                    Ir a ANTUAR directamente
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
